import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe, TitleCasePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { MessageService } from 'primeng/api';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { SelecaoPessoasPainel } from '@/shared/components/pessoa/selecao-pessoas-painel/selecao-pessoas-painel';
import { FotoPessoaPainel, OrdenacaoPessoasPainel } from '@/shared/components/pessoa/selecao-pessoas-painel/selecao-pessoas-painel.model';
import { filter, from } from 'rxjs';
import { AuthService } from '@/core/auth/auth.service';
import { RelatorioSelecaoService } from '@/core/api/sicom-api/registro/medico/relatorios/relatorio-selecao/relatorio-selecao.service';
import { RelatorioSelecaoJobService } from '@/core/services/relatorio-selecao-job.service';
import { MedicoListItem, MedicoRefRequest, RelatorioSelecaoLoteExportRequest, RelatorioSelecaoExportResponse, RelatorioSelecaoJobCreatedResponse } from '@/core/api/sicom-api/registro/medico/relatorios/relatorio-selecao/relatorio-selecao.model';
import { SelecionarProfissionais } from './selecionar-profissionais/selecionar-profissionais';

export interface ReportField {
    id: string;
    label: string;
    icon: string;
    category: string;
}

const ALL_FIELDS: ReportField[] = [
    { id: 'crm', label: 'CRM', icon: 'pi pi-id-card', category: 'Identificação' },
    { id: 'nome', label: 'Nome Completo', icon: 'pi pi-user', category: 'Identificação' },
    { id: 'cpf', label: 'CPF', icon: 'pi pi-credit-card', category: 'Identificação' },
    { id: 'dtNascimento', label: 'Data de Nascimento', icon: 'pi pi-calendar', category: 'Identificação' },
    { id: 'situacao', label: 'Situação', icon: 'pi pi-circle-fill', category: 'Situação' },
    { id: 'tipoInscricao', label: 'Tipo de Inscrição', icon: 'pi pi-tag', category: 'Situação' },
    { id: 'email', label: 'E-mail', icon: 'pi pi-envelope', category: 'Contato' },
    { id: 'especialidades', label: 'Especialidades', icon: 'pi pi-star', category: 'Seções' },
    { id: 'enderecos', label: 'Endereços e Telefones', icon: 'pi pi-map-marker', category: 'Seções' },
    { id: 'formacao', label: 'Formação Acadêmica', icon: 'pi pi-book', category: 'Seções' }
];

/** Seleção de campos com a qual a tela abre — também usada por "Reiniciar". */
const DEFAULT_FIELD_IDS = ALL_FIELDS.slice(0, 3).map(f => f.id);

/**
 * Tela de configuração e geração de relatório personalizado por seleção de médicos.
 * Orquestra a fila de análise, a configuração de campos e o disparo do fluxo
 * assíncrono de exportação PDF via Jasper e download de CSV.
 * A busca e seleção de profissionais ocorre no modal `SelecionarProfissionais`.
 */
@Component({
    selector: 'app-relatorio-selecao',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, FormsModule, Button, ToggleSwitch, RelatorioContainer, SelecaoPessoasPainel, SelecionarProfissionais],
    templateUrl: './relatorio-selecao.html',
    styleUrl: './relatorio-selecao.scss'
})
export class RelatorioSelecao implements OnInit {
    private readonly relatorioService = inject(RelatorioSelecaoService);
    private readonly authService = inject(AuthService);
    private readonly messageService = inject(MessageService);
    private readonly destroyRef = inject(DestroyRef);
    readonly jobService = inject(RelatorioSelecaoJobService);

    readonly job$ = this.jobService.job$;
    readonly ALL_FIELDS = ALL_FIELDS;

    // ── Modal de seleção ──────────────────────────────────────────────────────
    /** Controla a abertura do modal de busca e seleção de profissionais. */
    readonly modalVisible = signal(false);
    /** Elemento com foco antes de abrir o modal — recebe o foco de volta ao fechar (Esc/backdrop/ações). */
    private elementoAntesDoModal: HTMLElement | null = null;

    // ── Fila de análise ───────────────────────────────────────────────────────
    readonly selectedMedicos = signal<MedicoListItem[]>([]);

    // ── Campos do relatório ───────────────────────────────────────────────────
    /** Ordem de exibição de todos os campos — determina a ordem das colunas no relatório. */
    readonly fieldOrder      = signal<ReportField[]>([...ALL_FIELDS]);
    private readonly selectedIds = signal<Set<string>>(new Set(DEFAULT_FIELD_IDS));
    readonly selectedFields  = computed(() => this.fieldOrder().filter(f => this.selectedIds().has(f.id)));
    readonly availableFields = computed(() => this.fieldOrder().filter(f => !this.selectedIds().has(f.id)));

    /** Mensagem do estado vazio — aponta a condição que está de fato bloqueando o "Processar". */
    readonly textoSemDados = computed(() => {
        if (this.selectedMedicos().length === 0) {
            return 'Selecione médicos na fila e clique em Processar Relatório.';
        }
        if (this.selectedFields().length === 0) {
            return 'Selecione ao menos um campo em "Campos" e clique em Processar Relatório.';
        }
        return 'Clique em Processar Relatório para gerar o PDF.';
    });

    readonly isLoading        = signal(false);
    readonly pdfBlobs         = signal<Blob[]>([]);
    readonly camposExpandidos = signal(false);
    /** Nome de arquivo devolvido pelo backend (datado, igual ao padrão do CSV) para o PDF gerado. */
    readonly pdfFileName      = signal<string | null>(null);

    private readonly usuarioNome = signal('');

    get isPending(): boolean {
        return this.jobService.job?.status === 'PENDING';
    }

    constructor() {
        from(this.authService.getUserInfo())
            .pipe(takeUntilDestroyed())
            .subscribe({ next: (u) => this.usuarioNome.set(u.name || ''), error: () => {} });

        // acumula blobs de jobs assíncronos (202) quando o polling termina
        this.jobService.job$
            .pipe(
                takeUntilDestroyed(),
                filter((job) => job?.status === 'DONE' && !!job.pdfBlob)
            )
            .subscribe((job) => this.pdfBlobs.update((list) => [...list, job!.pdfBlob!]));
    }

    ngOnInit(): void {
        this.jobService.restaurarDeSessionStorage();
    }

    // ── Modal de seleção ──────────────────────────────────────────────────────
    abrirModal(): void {
        this.elementoAntesDoModal = document.activeElement as HTMLElement;
        this.modalVisible.set(true);
    }

    /** Fecha o modal (Esc, backdrop ou ações) devolvendo o foco ao elemento que o abriu. */
    onModalVisibleChange(visible: boolean): void {
        this.modalVisible.set(visible);
        if (!visible) {
            this.elementoAntesDoModal?.focus();
        }
    }

    /** Substitui a fila com a seleção confirmada pelo modal. */
    onProfissionaisConfirmados(medicos: MedicoListItem[]): void {
        this.selectedMedicos.set(medicos);
        this.descartarResultado();
    }

    // ── Fila de análise ───────────────────────────────────────────────────────
    /** Fila editada pelo `app-selecao-pessoas-painel` (adicionar/remover/limpar) — invalida o resultado já gerado. */
    onFilaAlterada(medicos: MedicoListItem[]): void {
        this.selectedMedicos.set(medicos);
        this.descartarResultado();
    }

    /** Accessors do `app-selecao-pessoas-painel` — identidade (CRM+UF), textos e ordenação. */
    protected readonly chaveDe = (m: MedicoListItem): string => `${m.nuCrm}-${m.sgUf}`;
    protected readonly nomeDe = (m: MedicoListItem): string => m.nmSolicitante;
    protected readonly subtituloDe = (m: MedicoListItem): string => `CRM ${m.nuCrmFormatado}`;
    /** Retrato por CRM + UF — médico não tem id interno disponível nesta tela. */
    protected readonly fotoDe = (m: MedicoListItem): FotoPessoaPainel => ({ idSolicitante: null, crm: m.nuCrm, uf: m.sgUf });
    protected readonly ordenacoesMedicos: OrdenacaoPessoasPainel<MedicoListItem>[] = [
        { rotulo: 'Nome', comparador: (a, b) => a.nmSolicitante.localeCompare(b.nmSolicitante, 'pt-BR') },
        { rotulo: 'CRM', comparador: (a, b) => a.nuCrm - b.nuCrm }
    ];

    // ── Campos do relatório ───────────────────────────────────────────────────
    /** Inverte inclusão de um campo no relatório. */
    toggleField(field: ReportField): void {
        this.selectedIds.update((s) => {
            const next = new Set(s);
            if (next.has(field.id)) next.delete(field.id);
            else next.add(field.id);
            return next;
        });
        this.descartarResultado();
    }

    /** Responde à mudança de estado do p-toggleswitch. */
    onFieldToggle(field: ReportField, checked: boolean): void {
        this.selectedIds.update((s) => {
            const next = new Set(s);
            if (checked) next.add(field.id);
            else next.delete(field.id);
            return next;
        });
        this.descartarResultado();
    }

    /** Posição 1-based do campo na lista de colunas ativas do relatório. */
    positionInReport(fieldId: string): number {
        return this.selectedFields().findIndex((f) => f.id === fieldId) + 1;
    }

    hasField(id: string): boolean {
        return this.selectedIds().has(id);
    }

    addField(field: ReportField): void {
        this.selectedIds.update((s) => {
            const n = new Set(s);
            n.add(field.id);
            return n;
        });
    }

    removeField(fieldOrId: ReportField | string): void {
        const id = typeof fieldOrId === 'string' ? fieldOrId : fieldOrId.id;
        this.selectedIds.update((s) => {
            const n = new Set(s);
            n.delete(id);
            return n;
        });
    }

    moveUp(index: number): void {
        if (index === 0) return;
        this.fieldOrder.update((arr) => {
            const next = [...arr];
            [next[index - 1], next[index]] = [next[index], next[index - 1]];
            return next;
        });
        this.descartarResultado();
    }

    moveDown(index: number): void {
        if (index >= this.fieldOrder().length - 1) return;
        this.fieldOrder.update((arr) => {
            const next = [...arr];
            [next[index], next[index + 1]] = [next[index + 1], next[index]];
            return next;
        });
        this.descartarResultado();
    }

    selecionarTodosCampos(): void {
        this.fieldOrder.set([...ALL_FIELDS]);
        this.selectedIds.set(new Set(ALL_FIELDS.map(f => f.id)));
        this.descartarResultado();
    }

    limparCampos(): void {
        this.selectedIds.set(new Set());
        this.descartarResultado();
    }

    /**
     * Invalida um relatório já gerado quando fila ou campos mudam depois do "Processar" —
     * sem isso o PDF/CSV antigo continua na tela sem refletir os parâmetros atuais.
     */
    private descartarResultado(): void {
        if (this.pdfBlobs().length === 0 && !this.jobService.job) return;
        this.pdfBlobs.set([]);
        this.pdfFileName.set(null);
        this.jobService.pararPolling();
        this.jobService.setJob(null);
        this.jobService.limparSessionStorage();
    }

    // ── Ações ─────────────────────────────────────────────────────────────────
    /** Restaura a tela ao estado com que ela abre: fila vazia e os mesmos 3 campos padrão. */
    reiniciar(): void {
        this.selectedMedicos.set([]);
        this.pdfBlobs.set([]);
        this.pdfFileName.set(null);
        this.fieldOrder.set([...ALL_FIELDS]);
        this.selectedIds.set(new Set(DEFAULT_FIELD_IDS));
        this.jobService.pararPolling();
        this.jobService.setJob(null);
        this.jobService.limparSessionStorage();
    }

    private buildLoteRequest(): RelatorioSelecaoLoteExportRequest {
        const medicos: MedicoRefRequest[] = this.selectedMedicos().map((m) => ({ nuCrm: m.nuCrm, sgUf: m.sgUf }));
        return {
            medicos,
            mostraNome: this.hasField('nome'),
            mostraCrm: this.hasField('crm'),
            mostraCpf: this.hasField('cpf'),
            mostraDtNascimento: this.hasField('dtNascimento'),
            mostraSituacao: this.hasField('situacao'),
            mostraTipoInscricao: this.hasField('tipoInscricao'),
            mostraEmail: this.hasField('email'),
            mostraEspecialidade: this.hasField('especialidades'),
            mostraEndereco:      this.hasField('enderecos'),
            mostraFormacao:      this.hasField('formacao'),
            ordemCampos:         this.selectedFields().map(f => f.id),
            emissor:             this.usuarioNome() || 'SICOM'
        };
    }

    /** Dispara a geração de um único PDF combinando todos os médicos selecionados (formato de tabela do legado). */
    processarRelatorio(): void {
        if (this.selectedMedicos().length === 0) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Selecione ao menos um médico para o relatório.' });
            return;
        }
        if (this.selectedFields().length === 0) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Selecione ao menos um campo para o relatório.' });
            return;
        }

        this.pdfBlobs.set([]);
        this.pdfFileName.set(null);
        this.isLoading.set(true);
        this.jobService.setJob({ jobId: '', status: 'PENDING', progress: 0 });

        this.relatorioService
            .exportarPdfLote(this.buildLoteRequest())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (resposta) => {
                    if (resposta.status === 200) {
                        const body = resposta.body as RelatorioSelecaoExportResponse;
                        const hasPdf = !!body?.pdf;
                        if (hasPdf) {
                            this.pdfBlobs.update(list => [...list, this.base64ToBlob(body.pdf!)]);
                            if (body.file_name) this.pdfFileName.set(body.file_name);
                        }
                        const msg = body?.message?.trim();
                        const foiIgnorado = !!msg && msg.includes('não encontrados');
                        this.messageService.add({
                            severity: hasPdf && !foiIgnorado ? 'success' : 'warn',
                            summary: hasPdf && !foiIgnorado ? 'Sucesso' : 'Atenção',
                            detail: msg || 'Relatório gerado.'
                        });
                    } else if (resposta.status === 202) {
                        const jobResp = resposta.body as RelatorioSelecaoJobCreatedResponse;
                        this.jobService.setJob({ jobId: jobResp.jobId, status: 'PENDING', progress: 0 });
                        this.messageService.add({
                            severity: 'info',
                            summary: 'Em processamento',
                            detail: 'Relatório sendo gerado em background. Você será notificado quando estiver pronto.',
                            life: 5000
                        });
                        this.jobService.iniciarPolling(jobResp.jobId);
                    }
                },
                error: (err: unknown) => {
                    this.isLoading.set(false);
                    const response = (err as { error?: RelatorioSelecaoExportResponse })?.error;
                    if (response?.pdf) {
                        this.pdfBlobs.update((list) => [...list, this.base64ToBlob(response.pdf!)]);
                        return;
                    }
                    if (response?.message?.trim()) {
                        this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: response.message!.trim() });
                        return;
                    }
                    this.jobService.updateStatus('ERROR');
                    this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao gerar o arquivo PDF.' });
                },
                complete: () => {
                    this.isLoading.set(false);
                    if (!this.jobService.job?.jobId) {
                        this.jobService.setJob(null);
                    }
                }
            });
    }

    /**
     * Exporta o CSV gerado no backend a partir dos mesmos dados usados no PDF — evita a
     * divergência de um CSV montado no cliente com apenas os campos trazidos pela busca.
     */
    exportarCsv(): void {
        if (this.selectedMedicos().length === 0) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Selecione ao menos um médico para exportar.' });
            return;
        }
        if (this.selectedFields().length === 0) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Selecione ao menos um campo para exportar.' });
            return;
        }

        this.isLoading.set(true);
        this.relatorioService.exportarCsvLote(this.buildLoteRequest())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: blob => {
                    this.isLoading.set(false);
                    const url = URL.createObjectURL(blob);
                    const a   = document.createElement('a');
                    a.href     = url;
                    a.download = `relatorio-selecao-${new Date().toISOString().slice(0, 10)}.csv`;
                    a.click();
                    setTimeout(() => URL.revokeObjectURL(url), 10_000);
                },
                // Toast local NECESSÁRIO aqui: a resposta é `blob`, então num erro o corpo é um Blob
                // e o UiErrorMessageBuilder não acha `detail`/`message`/`title` — marca `ignore` e o
                // interceptor global não exibe nada. Sem este toast a falha seria silenciosa.
                error: () => {
                    this.isLoading.set(false);
                    this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao gerar o arquivo CSV.' });
                }
            });
    }

    private base64ToBlob(base64: string): Blob {
        const normalized = base64.includes(',') ? base64.split(',')[1] : base64;
        const byteString = atob(normalized);
        const byteArray = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i += 1) {
            byteArray[i] = byteString.charCodeAt(i);
        }
        return new Blob([byteArray], { type: 'application/pdf' });
    }
}
